import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";
import { compact } from "lodash";
import { Box, Stack, Typography } from "@mui/material";
import {
  SetProductFragment,
  useGetAttributeProductsQuery,
  useGetGalleryWallCollectionsQuery,
  useGetProductsForGalleryWallLazyQuery,
} from "../../generated/storefront";
import { Loader } from "../../components/Loader";
import { getAvailableSizes, getSizeAndFrame } from "../../helpers/product";
import Button from "../../components/Button";
import useBasketActions from "../../hooks/useBasketActions";
import { colors } from "../../theme";
import { Frame } from "../../types/product";
import { formattedPrice } from "../../helpers/money";
import { useAppState } from "../../state";
import { Carousel, CarouselItem } from "../../components/SnapCarousel";
import { PlainMockUp } from "../../components/MockUps";
import FrameMockUp from "../../components/FrameMockUp";
import sofaMockUp from "../../assets/images/mock-ups/sofa.webp";
import bedMockUp from "../../assets/images/mock-ups/bed.webp";
import ScrollCarousel from "../../components/ScrollCarousel";
import { FrameType } from "../../helpers/basket";
import CollectionProductItem from "../../components/CollectionProductItem";
import CollectionCard from "../../components/Card/Collection";
import { getIdNumber } from "../../helpers/shopify";

const PrintSet = () => {
  const [searchParams] = useSearchParams();
  const productIds = useMemo(() => searchParams.get("products")?.split(",") || [], [searchParams]);
  const { isMobileScreen, selectedCountry } = useAppState();
  const { addOrCreateBasket, loading: loadingBasket, error } = useBasketActions();
  const [selectedVariants, setSelectedVariants] = useState<Record<string, { variantId: string; frame: string }>>({});
  const [getProducts, { data, loading }] = useGetProductsForGalleryWallLazyQuery();
  const products = useMemo(() => {
    const prods = data?.products?.nodes || [];
    return prods.length > 0
      ? [...prods].sort((a, b) => {
          const indexA = productIds.indexOf(getIdNumber(a.id));
          const indexB = productIds.indexOf(getIdNumber(b.id));
          return indexA - indexB;
        })
      : [];
  }, [data, productIds]);
  const { data: collectionsData } = useGetGalleryWallCollectionsQuery();
  const moreGalleryWalls = useMemo(() => collectionsData?.collections?.nodes || [], [collectionsData]);

  const { data: attributeProductsData } = useGetAttributeProductsQuery({
    variables: { country: selectedCountry },
  });
  const { standardFrameProduct, premiumFrameProduct } = attributeProductsData || {};

  const setSelectedVariant = (productId: string, variantId: string, frame: string) =>
    setSelectedVariants({ ...selectedVariants, [productId]: { variantId, frame } });

  const addAllItemsToBasket = () => {
    const items = Object.keys(selectedVariants).map((productId) => {
      const selectedVariantId = selectedVariants[productId].variantId;
      const product = products.find((p) => p.id === productId);
      if (!product) return null;
      const selectedVariant = product.variants.nodes.find((variant) => variant.id === selectedVariantId);
      if (!selectedVariant) return null;
      const { size } = getSizeAndFrame(selectedVariant.selectedOptions);
      const frame = selectedVariants[productId].frame.split("-")[0] as Frame;
      const boxFrame = selectedVariants[productId].frame.split("-")[1] === FrameType.Premium;
      return {
        type: "print" as const,
        merchandiseId: selectedVariantId,
        quantity: 1,
        frame,
        size,
        boxFrame,
        mounted: false,
      };
    });
    addOrCreateBasket(compact(items));
  };

  useEffect(() => {
    getProducts({
      variables: {
        query: `id:${productIds.join(" OR ")}`,
        country: selectedCountry,
      },
    });
  }, [productIds, selectedCountry, getProducts]);

  useEffect(() => {
    const defaultVariants: Record<string, { variantId: string; frame: string }> = {};
    products.forEach((product) => {
      const defaultVariant = product.variants.nodes.find((variant) => {
        const { size } = getSizeAndFrame(variant.selectedOptions);
        return size === getAvailableSizes(product.variants.nodes)[0];
      });
      if (defaultVariant)
        defaultVariants[product.id] = { variantId: defaultVariant.id, frame: `${Frame.Black}-${FrameType.Standard}` };
    });
    setSelectedVariants(defaultVariants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  if (!data || loading)
    return (
      <Box padding={2}>
        <Loader />
      </Box>
    );

  const getFramePrice = (product: SetProductFragment) => {
    const selectedFrame = selectedVariants[product.id]?.frame;
    if (!selectedFrame || selectedFrame === Frame.Unframed) return 0;
    const frameProduct = selectedFrame.split("-")[1] === FrameType.Standard ? standardFrameProduct : premiumFrameProduct;
    return Number(
      frameProduct?.variants.nodes.find(
        (variant) => variant.selectedOptions[0].value === getSizeAndFrame(variant.selectedOptions).size
      )?.price.amount || 0
    );
  };

  const subtotal = products.reduce((acc, product) => {
    const variant = product.variants.nodes.find((v) => v.id === selectedVariants[product.id]?.variantId);
    const framePrice = getFramePrice(product);
    return acc + (variant ? Number(variant.priceV2.amount) + framePrice : 0);
  }, 0);
  const currencyCode = products[0]?.variants.nodes[0].priceV2.currencyCode;
  const discountPct = 0.15;

  const getFrameColour = (product: SetProductFragment) => {
    const frameColour = selectedVariants[product.id]?.frame.split("-")[0] as Frame;
    return frameColour || Frame.Black;
  };

  const ProductMockUps = () => {
    return (
      <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="center" width="100%">
        {products.map((product, index) => {
          const width = match(index)
            .with(0, () => "calc(30% - 8px)")
            .with(1, () => "calc(35% - 8px)")
            .with(2, () => "calc(35% - 8px)")
            .with(3, () => "calc(20% - 8px)")
            .otherwise(() => "calc(20% - 8px)");
          const marginTop = match(index)
            .with(0, () => 2)
            .with(1, () => 0)
            .with(2, () => 2)
            .with(3, () => 0)
            .otherwise(() => 0);
          return (
            <Stack
              key={product.id}
              position="relative"
              width={width}
              marginTop={marginTop}
              justifyContent={index < 3 ? "flex-end" : "flex-start"}
            >
              <FrameMockUp frame={getFrameColour(product)} image={product.images.nodes[0]} shadow />
            </Stack>
          );
        })}
      </Stack>
    );
  };

  const SofaSetMockUp = () => (
    <Stack width="100%" height={{ xs: "100%", md: "auto" }} position="relative">
      <img src={sofaMockUp} alt="plain wall" width="100%" height="100%" style={{ objectFit: "cover" }} />
      <Stack
        direction="row"
        gap={2}
        position="absolute"
        top="26%"
        left="50%"
        width="60%"
        padding={{ xs: 2, md: 5 }}
        sx={{ transform: "translate(-50%, -50%)" }}
      >
        <ProductMockUps />
      </Stack>
    </Stack>
  );

  const BedSetMockUp = () => (
    <Stack width="100%" height={{ xs: "100%", md: "auto" }} position="relative">
      <img src={bedMockUp} alt="plain wall" width="100%" height="100%" style={{ objectFit: "cover" }} />
      <Stack
        direction="row"
        gap={2}
        position="absolute"
        top="30%"
        left="50%"
        width="60%"
        padding={{ xs: 2, md: 5 }}
        sx={{ transform: "translate(-50%, -50%)" }}
      >
        <ProductMockUps />
      </Stack>
    </Stack>
  );

  return (
    <>
      <Helmet>
        <title>Gallery Wall | GoodMood Prints</title>
      </Helmet>
      <Stack direction={{ xs: "column", md: "row" }} width="100%">
        {products.length > 0 ? (
          <>
            <Stack flexBasis={{ xs: "100%", md: "50%" }}>
              {isMobileScreen ? (
                products.length > 0 && (
                  <Box height="50vh">
                    <Carousel
                      items={[
                        {
                          key: "sofa-mockup",
                          component: <SofaSetMockUp />,
                        },
                        {
                          key: "bed-mockup",
                          component: <BedSetMockUp />,
                        },
                        ...products.map((product) => {
                          return {
                            key: product.id,
                            component: <PlainMockUp image={product.images.nodes[0]} frame={getFrameColour(product)} isMobile />,
                          };
                        }),
                      ]}
                      renderItem={({ item, isSnapPoint }) => {
                        return (
                          <CarouselItem key={item.key} isSnapPoint={isSnapPoint}>
                            {item.component}
                          </CarouselItem>
                        );
                      }}
                    />
                  </Box>
                )
              ) : (
                <Stack gap={0.2}>
                  <SofaSetMockUp />
                  <BedSetMockUp />
                </Stack>
              )}
            </Stack>
            <Stack flexBasis={{ xs: "100%", md: "50%" }}>
              <Box overflow={{ xs: "initial", md: "auto" }} paddingTop={2}>
                <Stack gap={3}>
                  <Typography variant="h2" component="h1" paddingX={{ xs: 2, md: 5 }}>
                    Gallery Wall
                  </Typography>
                  <Stack gap={2}>
                    {products.map((product, index) => (
                      <CollectionProductItem
                        key={product.id}
                        product={product}
                        setSelectedVariant={setSelectedVariant}
                        defaultFrame={Frame.Black}
                        lastChild={index === products.length - 1}
                      />
                    ))}
                  </Stack>
                </Stack>
                <Box
                  paddingY={1.5}
                  paddingX={{ xs: 2, md: 5 }}
                  bgcolor={colors.white}
                  position="sticky"
                  bottom={0}
                  borderTop={`1px solid ${colors.grey10}`}
                >
                  <Stack gap={{ xs: 1, md: 2 }} alignItems="center">
                    {products.length && (
                      <Stack gap={{ xs: 0.5, md: 1 }} direction="row" alignSelf="start">
                        <Typography
                          variant="h4"
                          alignSelf="start"
                          style={{ textDecoration: "line-through" }}
                          color={colors.grey60}
                        >
                          {formattedPrice(subtotal, currencyCode)}
                        </Typography>
                        <Typography variant="h4" color={colors.red}>
                          {formattedPrice(subtotal * (1 - discountPct), currencyCode)} · {discountPct * 100}% OFF
                        </Typography>
                      </Stack>
                    )}

                    <Button fullWidth onClick={addAllItemsToBasket} loading={loadingBasket}>
                      Add all to basket
                    </Button>
                    {error && <Typography color={colors.error}>{error}</Typography>}
                    <Typography variant="caption" align="center" color="text.secondary" fontSize={{ xs: 12, md: 14 }}>
                      No import fees for UK, EU, AUS & USA
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </>
        ) : (
          <Box padding={2} width="100%">
            <Loader />
          </Box>
        )}
      </Stack>
      <Box paddingY={{ xs: 2, md: 5 }}>
        <ScrollCarousel header="More Gallery Walls" cta={{ text: "View all", link: "/gallery-walls" }}>
          {moreGalleryWalls.map((c) => (
            <CollectionCard key={c.id} collection={c} imageHeight={isMobileScreen ? "50vw" : "25vw"} />
          ))}
        </ScrollCarousel>
      </Box>
    </>
  );
};

export default PrintSet;
